/**
 * AI Action Logger - Console logging for AI actions in single-player "With AI" mode
 * Logs each AI action with human-readable descriptions and results
 */

import { GameState, CardType, CardColor, PreparedCard, Player } from '../types';
import { AIAction } from './ai';

// Enable/disable AI logging - can be controlled externally
let aiLoggingEnabled = true;

export const enableAILogging = () => { aiLoggingEnabled = true; };
export const disableAILogging = () => { aiLoggingEnabled = false; };
export const isAILoggingEnabled = () => aiLoggingEnabled;

// Color codes for console styling
const STYLES = {
    header: 'color: #00bcd4; font-weight: bold; font-size: 14px;',
    action: 'color: #4caf50; font-weight: bold;',
    card: 'color: #ff9800; font-weight: bold;',
    essence: 'color: #9c27b0; font-weight: bold;',
    damage: 'color: #f44336; font-weight: bold;',
    heal: 'color: #8bc34a; font-weight: bold;',
    tower: 'color: #2196f3; font-weight: bold;',
    info: 'color: #607d8b;',
    result: 'color: #e91e63; font-weight: bold;',
};

const getCardEffect = (preparedCard: PreparedCard) => {
    const { card, rotation } = preparedCard;
    const finalRotation = (rotation % 360 + 360) % 360;
    const isFlipped = finalRotation > 90 && finalRotation < 270;
    const type = isFlipped && card.typeB ? card.typeB : card.type;
    const value = isFlipped && card.valueB ? card.valueB : card.value;
    return { type, value };
};

const getCardTypeName = (type: CardType): string => {
    const names: Partial<Record<CardType, string>> = {
        [CardType.Attack]: 'Attack',
        [CardType.Heal]: 'Heal',
        [CardType.Defense]: 'Defense',
        [CardType.PsySearch]: 'Psy-Search',
        [CardType.Steal]: 'Steal',
        [CardType.Energize]: 'Energize',
        [CardType.Disease]: 'Disease',
        [CardType.Shock]: 'Shock',
        [CardType.Hibernation]: 'Hibernation',
        [CardType.Dispel]: 'Dispel',
        [CardType.Pushback]: 'Pushback',
        [CardType.DoubleCross]: 'DoubleCross',
        [CardType.PsyBlock]: 'PsyBlock',
        [CardType.StealTower]: 'StealTower',
        [CardType.BodyUpgrade]: 'BodyUpgrade',
        [CardType.FortunesFavor]: 'FortunesFavor',
        [CardType.CursedIron]: 'CursedIron',
        [CardType.ArcaneBoots]: 'ArcaneBoots',
        [CardType.BrokenSecret]: 'BrokenSecret',
        [CardType.Chromabow]: 'Chromabow',
        [CardType.DiscerningEye]: 'DiscerningEye',
        [CardType.EnergySword]: 'EnergySword',
        [CardType.FlyingScythe]: 'FlyingScythe',
        [CardType.InfernoGauntlet]: 'InfernoGauntlet',
        [CardType.Larcendagger]: 'Larcendagger',
        [CardType.LifeDrain]: 'LifeDrain',
        [CardType.Frost]: 'Frost',
        [CardType.Invincible]: 'Invincible',
        [CardType.EyesOfMadness]: 'EyesOfMadness',
        [CardType.MadnessControl]: 'MadnessControl',
        [CardType.Counterblast]: 'Counterblast',
        [CardType.Protector]: 'Protector',
        [CardType.NaturalGrip]: 'NaturalGrip',
        [CardType.Trembling]: 'Trembling',
        [CardType.PlantedBomb]: 'PlantedBomb',
        [CardType.Ale]: 'Ale',
        [CardType.SealOfSilence]: 'SealOfSilence',
        [CardType.LifeCharger]: 'LifeCharger',
        [CardType.TwilightShift]: 'TwilightShift',
        [CardType.ThunderSprint]: 'ThunderSprint',
        [CardType.VoodooDoll]: 'VoodooDoll',
    };
    return names[type] || type.toString();
};

const getColorName = (color: CardColor): string => {
    const names: Record<CardColor, string> = {
        [CardColor.Red]: 'Red',
        [CardColor.Green]: 'Green',
        [CardColor.Black]: 'Black',
        [CardColor.Purple]: 'Purple',
        [CardColor.Yellow]: 'Yellow',
        [CardColor.Pink]: 'Pink',
    };
    return names[color] || color.toString();
};

/**
 * Log an AI action before it's executed
 */
export const logAIAction = (
    action: AIAction,
    gameState: GameState,
    aiPlayerId: number
): void => {
    if (!aiLoggingEnabled) return;

    const aiPlayer = gameState.players.find(p => p.id === aiPlayerId);
    if (!aiPlayer) return;

    const timestamp = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', STYLES.header);
    console.log(`%c🤖 AI ACTION [${timestamp}]`, STYLES.header);
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', STYLES.header);

    switch (action.type) {
        case 'ATTEMPT_ACTIVATION': {
            const pc = aiPlayer.prepared.find(p => p.card.id === action.cardId);
            if (pc) {
                const effect = getCardEffect({ ...pc, rotation: action.rotation });
                const cardName = pc.card.faceSlug || getCardTypeName(effect.type);
                const valuePart = effect.value ? ` (Value: ${effect.value})` : '';
                const essenceCost = pc.card.essenceCost ? ` [Essence Cost: ${pc.card.essenceCost}]` : '';
                const rotationInfo = action.rotation ? ` (Rotation: ${action.rotation}°)` : '';

                console.log(`%c▶ Action: %cATTEMPT ACTIVATION`, STYLES.info, STYLES.action);
                console.log(`%c  Card: %c${cardName}${valuePart}${essenceCost}${rotationInfo}`, STYLES.info, STYLES.card);
                console.log(`%c  Effect: %c${getCardTypeName(effect.type)}`, STYLES.info, STYLES.card);

                if (action.targetId !== undefined) {
                    const target = action.targetId === aiPlayerId ? 'Self' : 'Opponent';
                    console.log(`%c  Target: %c${target}`, STYLES.info, STYLES.damage);
                }
            }
            break;
        }

        case 'COUNTER_ACTIVATION': {
            const pc = aiPlayer.prepared.find(p => p.card.id === action.cardId);
            if (pc) {
                const effect = getCardEffect({ ...pc, rotation: action.rotation });
                const cardName = pc.card.faceSlug || getCardTypeName(effect.type);
                const topActivation = gameState.activations[gameState.activations.length - 1];
                const attackingCardName = topActivation?.attackingCard?.card.faceSlug || 'Unknown';

                console.log(`%c▶ Action: %cCOUNTER ACTIVATION`, STYLES.info, STYLES.action);
                console.log(`%c  Counter Card: %c${cardName}`, STYLES.info, STYLES.card);
                console.log(`%c  Counter Effect: %c${getCardTypeName(effect.type)}`, STYLES.info, STYLES.card);
                console.log(`%c  Countering: %c${attackingCardName}`, STYLES.info, STYLES.damage);
            }
            break;
        }

        case 'ACCEPT_ACTIVATION': {
            const topActivation = gameState.activations[gameState.activations.length - 1];
            const attackingCardName = topActivation?.attackingCard?.card.faceSlug || 'Unknown';
            console.log(`%c▶ Action: %cACCEPT ACTIVATION`, STYLES.info, STYLES.action);
            console.log(`%c  Accepting: %c${attackingCardName}`, STYLES.info, STYLES.damage);
            break;
        }

        case 'PASS': {
            console.log(`%c▶ Action: %cPASS TURN`, STYLES.info, STYLES.action);
            console.log(`%c  Reason: %cNo better moves available or waiting for opponent`, STYLES.info, STYLES.info);
            break;
        }

        case 'DISCARD_STASH': {
            const stashCard = aiPlayer.prepared.find(pc => pc.card.id === action.cardId)?.card
                || aiPlayer.hand.find(c => c.id === action.cardId);
            if (stashCard) {
                const isPrepared = aiPlayer.prepared.some(pc => pc.card.id === action.cardId);
                // Burning a prepared stash card gives 4 essence, from hand gives essenceCost (usually 2-3)
                const essenceGain = isPrepared ? 4 : (stashCard.essenceCost || 4);
                const cardName = stashCard.faceSlug || getCardTypeName(stashCard.type);

                console.log(`%c▶ Action: %cBURN STASH CARD FOR ESSENCE`, STYLES.info, STYLES.action);
                console.log(`%c  Card: %c${cardName}`, STYLES.info, STYLES.card);
                console.log(`%c  Location: %c${isPrepared ? 'Prepared' : 'Hand'}`, STYLES.info, STYLES.info);
                console.log(`%c  Essence Gained: %c+${essenceGain}`, STYLES.info, STYLES.essence);
                console.log(`%c  AI Essence: %c${aiPlayer.essence} → ${aiPlayer.essence + essenceGain}`, STYLES.info, STYLES.essence);
            }
            break;
        }

        case 'DISCARD_HAND_FOR_ESSENCE': {
            const card = aiPlayer.hand.find(c => c.id === action.cardId);
            if (card) {
                const cardName = card.faceSlug || getCardTypeName(card.type);
                console.log(`%c▶ Action: %cDISCARD CARD FOR ESSENCE`, STYLES.info, STYLES.action);
                console.log(`%c  Card: %c${cardName} (${getColorName(card.color)})`, STYLES.info, STYLES.card);
                console.log(`%c  Essence Gained: %c+1`, STYLES.info, STYLES.essence);
                console.log(`%c  AI Essence: %c${aiPlayer.essence} → ${aiPlayer.essence + 1}`, STYLES.info, STYLES.essence);
            }
            break;
        }

        case 'START_TOWER_PURCHASE': {
            console.log(`%c▶ Action: %cSTART TOWER PURCHASE`, STYLES.info, STYLES.action);
            console.log(`%c  AI Towers: %c${aiPlayer.towers.length}/5`, STYLES.info, STYLES.tower);
            break;
        }

        case 'PURCHASE_TOWER': {
            console.log(`%c▶ Action: %cPURCHASE TOWER`, STYLES.info, STYLES.action);
            console.log(`%c  Color: %c${getColorName(action.color)}`, STYLES.info, STYLES.tower);
            console.log(`%c  AI Towers: %c${aiPlayer.towers.length} → ${aiPlayer.towers.length + 1}`, STYLES.info, STYLES.tower);
            break;
        }

        case 'OPEN_TOWER_CHANGE_MENU': {
            console.log(`%c▶ Action: %cOPEN TOWER CHANGE MENU`, STYLES.info, STYLES.action);
            console.log(`%c  Tower Index: %c${action.towerIndex}`, STYLES.info, STYLES.tower);
            break;
        }

        case 'CHANGE_TOWER_COLOR': {
            console.log(`%c▶ Action: %cCHANGE TOWER COLOR`, STYLES.info, STYLES.action);
            console.log(`%c  New Color: %c${getColorName(action.color)}`, STYLES.info, STYLES.tower);
            break;
        }

        case 'PREPARE_CARD': {
            const card = aiPlayer.hand.find(c => c.id === action.cardId);
            if (card) {
                const cardName = card.faceSlug || getCardTypeName(card.type);
                const essenceCost = card.essenceCost ? ` [Essence Cost: ${card.essenceCost}]` : '';
                console.log(`%c▶ Action: %cPREPARE CARD`, STYLES.info, STYLES.action);
                console.log(`%c  Card: %c${cardName}${essenceCost}`, STYLES.info, STYLES.card);
                console.log(`%c  Rotation: %c${action.rotation}°`, STYLES.info, STYLES.info);
            }
            break;
        }

        default:
            console.log(`%c▶ Action: %c${(action as any).type}`, STYLES.info, STYLES.action);
    }

    // Log AI state summary
    console.log('%c──────────────────────────────────────────', STYLES.info);
    console.log(`%c  AI State: %cHP: ${aiPlayer.health}/${aiPlayer.maxHealth} | Essence: ${aiPlayer.essence} | Towers: ${aiPlayer.towers.length}/5 | Hand: ${aiPlayer.hand.length} | Prepared: ${aiPlayer.prepared.length}`, STYLES.info, STYLES.info);
};

/**
 * Log the result after an AI action is executed
 */
export const logAIActionResult = (
    action: AIAction,
    prevState: GameState,
    newState: GameState,
    aiPlayerId: number
): void => {
    if (!aiLoggingEnabled) return;

    const prevAI = prevState.players.find(p => p.id === aiPlayerId);
    const newAI = newState.players.find(p => p.id === aiPlayerId);
    const prevOpp = prevState.players.find(p => p.id !== aiPlayerId);
    const newOpp = newState.players.find(p => p.id !== aiPlayerId);

    if (!prevAI || !newAI || !prevOpp || !newOpp) return;

    const changes: string[] = [];

    // Detect health changes
    if (newAI.health !== prevAI.health) {
        const diff = newAI.health - prevAI.health;
        changes.push(`AI HP: ${prevAI.health} → ${newAI.health} (${diff > 0 ? '+' : ''}${diff})`);
    }
    if (newOpp.health !== prevOpp.health) {
        const diff = newOpp.health - prevOpp.health;
        changes.push(`Opponent HP: ${prevOpp.health} → ${newOpp.health} (${diff > 0 ? '+' : ''}${diff})`);
    }

    // Detect essence changes
    if (newAI.essence !== prevAI.essence) {
        const diff = newAI.essence - prevAI.essence;
        changes.push(`AI Essence: ${prevAI.essence} → ${newAI.essence} (${diff > 0 ? '+' : ''}${diff})`);
    }

    // Detect shield changes
    if (newAI.shield !== prevAI.shield) {
        changes.push(`AI Shield: ${prevAI.shield} → ${newAI.shield}`);
    }
    if (newOpp.shield !== prevOpp.shield) {
        changes.push(`Opponent Shield: ${prevOpp.shield} → ${newOpp.shield}`);
    }

    // Detect tower changes
    if (newAI.towers.length !== prevAI.towers.length) {
        changes.push(`AI Towers: ${prevAI.towers.length} → ${newAI.towers.length}`);
    }
    if (newOpp.towers.length !== prevOpp.towers.length) {
        changes.push(`Opponent Towers: ${prevOpp.towers.length} → ${newOpp.towers.length}`);
    }

    // Detect hand changes
    if (newAI.hand.length !== prevAI.hand.length) {
        changes.push(`AI Hand: ${prevAI.hand.length} → ${newAI.hand.length}`);
    }

    // Detect prepared cards changes
    if (newAI.prepared.length !== prevAI.prepared.length) {
        changes.push(`AI Prepared: ${prevAI.prepared.length} → ${newAI.prepared.length}`);
    }

    // Detect effect cards changes
    if (newAI.effectCards.length !== prevAI.effectCards.length) {
        changes.push(`AI Effects: ${prevAI.effectCards.length} → ${newAI.effectCards.length}`);
    }
    if (newOpp.effectCards.length !== prevOpp.effectCards.length) {
        changes.push(`Opponent Effects: ${prevOpp.effectCards.length} → ${newOpp.effectCards.length}`);
    }

    if (changes.length > 0) {
        console.log('%c📊 RESULT:', STYLES.result);
        changes.forEach(change => {
            console.log(`%c   • ${change}`, STYLES.result);
        });
    }
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n', STYLES.header);
};

/**
 * Log AI preparation phase actions
 */
export const logAIPreparation = (
    gameState: GameState,
    aiPlayerId: number,
    preparedCards: { cardName: string; rotation: number }[]
): void => {
    if (!aiLoggingEnabled) return;

    const timestamp = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const aiPlayer = gameState.players.find(p => p.id === aiPlayerId);

    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', STYLES.header);
    console.log(`%c🌅 AI PREPARATION PHASE [${timestamp}]`, STYLES.header);
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', STYLES.header);

    if (preparedCards.length === 0) {
        console.log(`%c  AI prepared no cards this turn`, STYLES.info);
    } else {
        console.log(`%c  AI prepared ${preparedCards.length} card(s):`, STYLES.action);
        preparedCards.forEach((pc, i) => {
            console.log(`%c   ${i + 1}. ${pc.cardName}${pc.rotation ? ` (Rotation: ${pc.rotation}°)` : ''}`, STYLES.card);
        });
    }

    if (aiPlayer) {
        console.log('%c──────────────────────────────────────────', STYLES.info);
        console.log(`%c  AI State: HP: ${aiPlayer.health}/${aiPlayer.maxHealth} | Essence: ${aiPlayer.essence} | Hand: ${aiPlayer.hand.length} | Prepared: ${aiPlayer.prepared.length}`, STYLES.info);
    }
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n', STYLES.header);
};

/**
 * Log AI special phase handling
 */
export const logAISpecialAction = (
    phase: string,
    details: Record<string, any>,
    aiPlayerId: number
): void => {
    if (!aiLoggingEnabled) return;

    const timestamp = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', STYLES.header);
    console.log(`%c🎯 AI SPECIAL ACTION [${timestamp}]`, STYLES.header);
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', STYLES.header);
    console.log(`%c  Phase: %c${phase}`, STYLES.info, STYLES.action);

    Object.entries(details).forEach(([key, value]) => {
        console.log(`%c  ${key}: %c${value}`, STYLES.info, STYLES.card);
    });

    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n', STYLES.header);
};
